import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../utils/api";
import { FaSpinner, FaTrash, FaExternalLinkAlt } from "react-icons/fa";

const STATUS_OPTIONS = ["new", "in-progress", "resolved"];
const STATUS_STYLES = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    "in-progress": "bg-yellow-100 text-yellow-700 border-yellow-200",
    resolved: "bg-green-100 text-green-700 border-green-200",
};

function EnquiryModal({ enquiry, onClose, onStatusUpdate }) {
    const [status, setStatus] = useState(enquiry.status);
    const [notes, setNotes] = useState(enquiry.notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const res = await api.updateEnquiryStatus(enquiry._id, { status, notes });
            onStatusUpdate(res.data);
            onClose();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-800">Enquiry Details</h2>
                    <p className="text-slate-400 text-xs mt-1">{new Date(enquiry.createdAt).toLocaleString()}</p>
                </div>

                <div className="p-6 space-y-4">
                    {[
                        ["Full Name", enquiry.name],
                        ["Email", enquiry.email],
                        ["Phone", enquiry.phone || "—"],
                        ["Visa / Service", enquiry.visaType || "—"],
                        ["Target Country", enquiry.country || "—"],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                            <p className="text-slate-800 text-sm">{value}</p>
                        </div>
                    ))}

                    <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Message</p>
                        <p className="text-slate-700 text-sm bg-slate-50 rounded-xl p-4 leading-relaxed">{enquiry.message}</p>
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Status</p>
                        <div className="flex gap-2 flex-wrap">
                            {STATUS_OPTIONS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setStatus(s)}
                                    className={`capitalize text-sm px-4 py-1.5 rounded-full border font-medium transition ${status === s ? STATUS_STYLES[s] + " ring-2 ring-offset-1 ring-current" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                                        }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 block">Admin Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Internal notes..."
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        />
                    </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex gap-3">
                    <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-50 transition">
                        Cancel
                    </button>
                    <a
                        href={`mailto:${enquiry.email}`}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition"
                    >
                        <FaExternalLinkAlt className="text-xs" /> Reply
                    </a>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminEnquiries() {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filterStatus ? `?status=${filterStatus}` : "";
            const res = await api.getEnquiries(params);
            setEnquiries(res.data);
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const handleStatusUpdate = (updated) => {
        setEnquiries((prev) => prev.map((e) => (e._id === updated._id ? updated : e)));
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
        await api.deleteEnquiry(id);
        setEnquiries((prev) => prev.filter((e) => e._id !== id));
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Enquiries</h1>
                    <p className="text-slate-500 text-sm mt-1">{enquiries.length} enquiries found</p>
                </div>
                {/* Filter */}
                <div className="flex gap-2 flex-wrap">
                    {["", ...STATUS_OPTIONS].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setFilterStatus(s)}
                            className={`capitalize text-sm px-4 py-2 rounded-full font-medium transition border ${filterStatus === s
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                                }`}
                        >
                            {s || "All"}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-40">
                        <FaSpinner className="animate-spin text-blue-600 text-2xl" />
                    </div>
                ) : enquiries.length === 0 ? (
                    <p className="text-center text-slate-400 py-16">No enquiries found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    {["Name", "Email", "Service", "Status", "Date", ""].map((h) => (
                                        <th key={h} className="text-left px-5 py-3.5 text-slate-500 font-semibold text-xs uppercase tracking-wide">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {enquiries.map((e) => (
                                    <tr
                                        key={e._id}
                                        className="hover:bg-slate-50 transition cursor-pointer"
                                        onClick={() => setSelected(e)}
                                    >
                                        <td className="px-5 py-4 font-semibold text-slate-800">{e.name}</td>
                                        <td className="px-5 py-4 text-slate-500 max-w-[180px] truncate">{e.email}</td>
                                        <td className="px-5 py-4 text-slate-500">{e.visaType || "—"}</td>
                                        <td className="px-5 py-4">
                                            <span className={`capitalize text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_STYLES[e.status]}`}>
                                                {e.status}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                                            {new Date(e.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                        </td>
                                        <td className="px-5 py-4" onClick={(ev) => ev.stopPropagation()}>
                                            <button
                                                onClick={() => handleDelete(e._id)}
                                                className="text-slate-300 hover:text-red-500 transition p-1.5 rounded-lg hover:bg-red-50"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selected && (
                <EnquiryModal
                    enquiry={selected}
                    onClose={() => setSelected(null)}
                    onStatusUpdate={handleStatusUpdate}
                />
            )}
        </div>
    );
}
